import type { ApolloClient } from '@apollo/client';
import { reportError } from '@medcom/shared-ui/utils/telemetry.ts';
import type { App } from 'antd';
import type { User } from 'oidc-client-ts';
import type { NavigateFunction } from 'react-router';
import { RequestStatus } from '../graphql/generated/graphql';
import { startReorder } from '../hooks/startReorder';
import { REQUEST_FOR_REORDER_QUERY } from '../hooks/startReorder.gql';
import { useAppStore } from '../hooks/store/useAppStore';
import { useCurrentContextStore } from '../hooks/store/useCurrentContextStore';
import { hasFrontDeskRole } from '../utils/user';
import { AIS_SESSION_INFO_QUERY } from './AisSessionInfo.gql';
import { AIS_LAUNCH_ID_KEY } from './aisSignin';
import type { AisImportResult } from './redeemAisContext';
import { switchToZuweiser } from './switchToZuweiser';

type ModalApi = ReturnType<typeof App.useApp>['modal'];
type MessageApi = ReturnType<typeof App.useApp>['message'];

const REPLACE_TEXT = 'Wollen Sie die aktuelle Anforderung abbrechen und die Anforderung aus Ihrem AIS öffnen?';

const rememberLaunchId = (launchId: string | null) =>
  launchId ? sessionStorage.setItem(AIS_LAUNCH_ID_KEY, launchId) : sessionStorage.removeItem(AIS_LAUNCH_ID_KEY);

// as in startReorder, front desk keeps the Zuweiser it works for, if it has one yet
const keepsZuweiser = (reorder: boolean, user: User | null | undefined) =>
  reorder && hasFrontDeskRole(user) && !!useCurrentContextStore.getState().currentDoctorId;

// the request exists already: the prefill is ignored, and a sent request is view-only
const openExistingRequest = async (
  requestId: string,
  client: ApolloClient,
  navigate: NavigateFunction,
  { openReorder, reorderRequested }: Pick<AisImportResult, 'openReorder' | 'reorderRequested'>,
  message: MessageApi,
  user: User | null | undefined
) => {
  const { data } = await client.query({
    query: REQUEST_FOR_REORDER_QUERY,
    variables: { id: requestId },
    fetchPolicy: 'network-only',
  });
  const request = data?.request;
  const sent = request?.status === RequestStatus.SENT || request?.status === RequestStatus.RECEIVED;
  // the lists and catalogs load for the current Zuweiser; the redeem granted the request's to this session
  if (request && !keepsZuweiser(sent && openReorder, user)) {
    await switchToZuweiser(request.doctor.id, client, user);
  }
  if (sent) {
    if (openReorder && request) {
      useAppStore.getState().reset();
      startReorder(request, navigate, { user, replace: true });
      return;
    }
    if (reorderRequested) {
      message.warning('Nachforderung nicht mehr möglich');
    }
    navigate('/gesendete-anforderungen', { replace: true, state: { openDetails: requestId } });
    return;
  }
  const { reset, setRequestIdForUpdate, setExecuteRequestCopy } = useAppStore.getState();
  reset();
  setRequestIdForUpdate(requestId);
  setExecuteRequestCopy(true);
  navigate('/offene-anforderungen', { replace: true });
};

// The import asks the same in PatientImportModal
const mayReplaceStarted = (modal: ModalApi): Promise<boolean> =>
  useAppStore.getState().patientData
    ? new Promise(resolve =>
        modal.confirm({
          title: 'Laboranforderung aus dem AIS',
          content: REPLACE_TEXT,
          okText: 'Ja',
          cancelText: 'Nein',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      )
    : Promise.resolve(true);

export const openRedeemed = async (
  result: AisImportResult,
  client: ApolloClient,
  navigate: NavigateFunction,
  setTempAisImportResult: (result: AisImportResult) => void,
  modal: ModalApi,
  message: MessageApi,
  user: User | null | undefined
) => {
  // the redeem made this link's session the one the login prints to, even if the user keeps the current Anforderung
  client.refetchQueries({ include: [AIS_SESSION_INFO_QUERY] }).catch(reportError);
  if (result.openRequestId && !(await mayReplaceStarted(modal))) return;
  rememberLaunchId(result.aisLaunchId);
  if (result.openRequestId) {
    await openExistingRequest(result.openRequestId, client, navigate, result, message, user);
  } else {
    setTempAisImportResult(result);
  }
};
